import { useCallback } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  App,
  AppWithImplementationsMeta,
  Connection,
  useApp,
  useHydrateAppsWithImplementationsMeta,
  useRefetchAuthentications,
} from "@zapier/client";
import useCreateAppConnection from "./useCreateAppConnection";
import { useNotification } from "@zapier/utils";

export function useReconnectConnection(connection: Connection) {
  const { refetchAuthentications } = useRefetchAuthentications();
  const createNewConnection = useCreateAppConnection();
  const { createNotification } = useNotification();
  const t = useTranslations("connections-reconnect-modal");

  const { app, isLoading: isAppLoading } = useApp({
    slug: connection?.implementation?.slug || "",
    selectedApi: connection?.selectedApi || "",
    includeImplementation: true,
  });

  const { apps: hydratedApps, isLoading: isHydratedAppsLoading } =
    useHydrateAppsWithImplementationsMeta({
      apps: [app as App],
      onlyAppsWithAuth: false,
    });

  const isLoading = isAppLoading || isHydratedAppsLoading;

  const connectionApp = hydratedApps[0];

  const hasAuthInfo = !!connectionApp?.implementationMeta?.authType;

  const isAppUnavailable = !connection?.id || (!isLoading && !hasAuthInfo);

  const reconnect = useCallback(async () => {
    if (isLoading || isAppUnavailable) {
      createNotification({
        message: t("notifications.reconnect-error", {
          connectionTitle: connection?.title || "",
        }),
        type: "error",
      });
      return;
    }

    // `success` (not the presence of an id) is the source of truth: some
    // OAuth apps complete successfully without ever returning an authId.
    const { success } = await createNewConnection(
      connectionApp as AppWithImplementationsMeta,
      connection,
    );

    if (success) {
      createNotification({
        message: t("notifications.reconnect-success", {
          connectionTitle: connection?.title || "",
        }),
        type: "success",
      });
      refetchAuthentications();
    }
  }, [
    connection,
    connectionApp,
    createNewConnection,
    createNotification,
    refetchAuthentications,
    isLoading,
    isAppUnavailable,
    t,
  ]);

  return { reconnect, isAppUnavailable, isLoading };
}
